import { FileParameter, TemplateType, TemplatesClient } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation } from "@tanstack/react-query";

const templatesClient = new TemplatesClient(undefined, axios);

// Response types - Manual definitions since TemplatesController returns 'object'
// These match the C# models in ProfileMappingTemplateService.cs
// Property names use camelCase to match JSON serialization from C# PascalCase
export interface ImportProfileMappingResponse {
  // Configuration-specific results
  autoMappingResult?: ImportProfileMappingResult;
  nonModelledResult?: ImportProfileMappingResult;
  rebarEstimationResult?: ImportProfileMappingResult;
  
  // Global properties
  validationFailures?: ValidationFailure[];
  isValid?: boolean;
  profileName?: string;
  profileExists?: boolean;
}

export interface ImportProfileMappingResult {
  successItems?: ImportProfileMapping[];
  failureItems?: ImportProfileMappingFailure[];
}

export interface ImportProfileMapping {
  row?: number;
  configurationType?: string;  // "AutoMapping" | "NonModelled" | "RebarEstimation"
  
  // Core identification fields
  revitCategory?: string;
  grsFamilyName?: string;
  typeName?: string;
  mpa?: string;
  veracityId?: string;
  location?: string;
  referencePeriod?: string;
  
  // Extended metadata fields
  system?: string;
  subSystem?: string;
  attribute1?: string;
  attribute2?: string;
  attribute3?: string;
  part?: string; // Non-Modelled/Rebar Part column
  
  // Configuration-specific fields
  percentageValue?: number;  // For Non-Modelled/Rebar
  
  // Legacy fields for Veracity mapping
  veracityTitle?: string;
  veracitySubtitle?: string;
  
  // Processing results
  mappedVeracityMaterial?: any; // Co2MaterialType
  status?: string;
  importStatus?: string;
  importStatusMessage?: string;
  co2MaterialTypeId?: string;
  cadModelCategoryId?: string;
}

export interface ImportProfileMappingFailure {
  importProfileMapping?: ImportProfileMapping;
  errors?: { [key: string]: string[] };
}

// ValidationFailure matches FluentValidation.Results.ValidationFailure from backend
export interface ValidationFailure {
  propertyName?: string;
  errorMessage?: string;
}

type UploadProfileMappingVariables = {
  commitChanges: boolean;
  commitAfterWarning: boolean;
  fileParameter: FileParameter;
};

export const useUploadProfileMapping = (options?: {
  onSuccess?: (data: ImportProfileMappingResponse) => void | Promise<void>;
}) => {
  return useMutation<ImportProfileMappingResponse, Error, UploadProfileMappingVariables>({
    mutationFn: async ({
      commitChanges,
      commitAfterWarning,
      fileParameter,
    }: UploadProfileMappingVariables) => {
      
      return await templatesClient.uploadTemplate(TemplateType.ProfileMapping, commitChanges, commitAfterWarning, false, fileParameter);
    },
    onSuccess: async (data: ImportProfileMappingResponse, variables: UploadProfileMappingVariables) => {
      console.log("useUploadProfileMapping variables: ", variables);

      if (variables.commitChanges) {
        // When we commit changes, reset queries
        await queryClient.resetQueries({ queryKey: ["profiles"], exact: false });
        await queryClient.resetQueries({ queryKey: ["modelCommands"], exact: false });
      } else {
        // When we are only doing validation, process the response
        options?.onSuccess?.(data);
      }
    },
  });
};

